import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import CommonDialog, { CommonDialogAction } from "@/Components/Common/CommonDialog";
import {
  DialogConfirmOptions,
  DialogOptions,
  dialogService,
  registerDialogBridge,
} from "@/utils/dialogService";

type PendingDialogRequest =
  | {
      kind: "confirm";
      options: DialogConfirmOptions;
      resolve: (value: boolean) => void;
    }
  | {
      kind: "warning" | "error";
      options: DialogOptions;
      resolve: () => void;
    };

interface DialogContextType {
  confirm: typeof dialogService.confirm;
  warning: typeof dialogService.warning;
  error: typeof dialogService.error;
}

const DialogContext = createContext<DialogContextType | null>(null);

function isConfirmRequest(request: PendingDialogRequest | undefined): request is Extract<PendingDialogRequest, { kind: "confirm" }> {
  return request?.kind === "confirm";
}

export const DialogProvider = ({ children }: { children: React.ReactNode }) => {
  const [queue, setQueue] = useState<PendingDialogRequest[]>([]);

  const current = queue[0];

  const removeCurrent = useCallback(() => {
    setQueue((prev) => prev.slice(1));
  }, []);

  const enqueueConfirm = useCallback((options: DialogConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setQueue((prev) => [...prev, { kind: "confirm", options, resolve }]);
    });
  }, []);

  const enqueueWarning = useCallback((options: DialogOptions) => {
    return new Promise<void>((resolve) => {
      setQueue((prev) => [...prev, { kind: "warning", options, resolve }]);
    });
  }, []);

  const enqueueError = useCallback((options: DialogOptions) => {
    return new Promise<void>((resolve) => {
      setQueue((prev) => [...prev, { kind: "error", options, resolve }]);
    });
  }, []);

  useEffect(() => {
    registerDialogBridge({
      confirm: enqueueConfirm,
      warning: enqueueWarning,
      error: enqueueError,
    });
    return () => {
      registerDialogBridge(null);
    };
  }, [enqueueConfirm, enqueueWarning, enqueueError]);

  const resolveAndClose = useCallback(
    (confirmed: boolean) => {
      if (!current) return;
      if (isConfirmRequest(current)) {
        current.resolve(confirmed);
      } else {
        current.resolve();
      }
      removeCurrent();
    },
    [current, removeCurrent]
  );

  const handleClose = useCallback(() => {
    resolveAndClose(false);
  }, [resolveAndClose]);

  const actions = useMemo<CommonDialogAction[]>(() => {
    if (!current) return [];

    if (isConfirmRequest(current)) {
      const isDestructive = current.options.destructive;
      return [
        {
          label: current.options.cancelLabel || "취소",
          color: "light",
          closeOnClick: false,
          onClick: () => {
            resolveAndClose(false);
          },
        },
        {
          label: current.options.confirmLabel || "확인",
          color: isDestructive ? "danger" : "primary",
          closeOnClick: false,
          onClick: () => {
            resolveAndClose(true);
          },
        },
      ];
    }

    return [
      {
        label: current.options.confirmLabel || "확인",
        color: current.kind === "error" ? "danger" : "warning",
        closeOnClick: false,
        onClick: () => {
          resolveAndClose(true);
        },
      },
    ];
  }, [current, resolveAndClose]);

  const contextValue = useMemo<DialogContextType>(
    () => ({
      confirm: dialogService.confirm,
      warning: dialogService.warning,
      error: dialogService.error,
    }),
    []
  );

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
      <CommonDialog
        isOpen={Boolean(current)}
        onClose={handleClose}
        title={current?.options.title}
        description={current?.options.description}
        tone={current?.options.tone ?? (current?.kind === "error" ? "error" : "warning")}
        actions={actions}
        size="md"
        centered
      />
    </DialogContext.Provider>
  );
};

export const useDialog = (): DialogContextType => {
  const context = useContext(DialogContext);
  if (context) return context;

  return {
    confirm: dialogService.confirm,
    warning: dialogService.warning,
    error: dialogService.error,
  };
};
